import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FiMusic, FiPlus, FiTrash2, FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import FormField from '../components/FormField';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import { db, functions, storage } from '../firebase';
import { createSquareCroppedImage } from '../utils/imageCrop';
import { createVersionToken, useStorageUrl } from '../utils/storageMedia';
import layoutStyles from '../styles/layout.module.css';
import styles from './TemporaryArtistsAdmin.module.css';

const PROFILE_ORIGINAL_MAX_SIZE = 2200;
const PROFILE_IMAGE_SIZE = 1000;
const PROFILE_THUMB_SIZE = 200;
const PROFILE_TINY_SIZE = 50;

const EMPTY_FORM = {
  artistName: '',
  description: '',
  instagram: '',
  twitter: '',
  tiktok: '',
  youtube: '',
  website: '',
};

const EMPTY_PHOTO = {
  originalPath: '',
  path: '',
  thumbPath: '',
  tinyPath: '',
};

const EMPTY_AUDIO = {
  url: '',
  path: '',
};

function centeredCropForImage(image) {
  const side = Math.min(image.width, image.height);
  return {
    x: Math.floor((image.width - side) / 2),
    y: Math.floor((image.height - side) / 2),
    width: side,
    height: side,
  };
}

async function createSquareImageSet(file, variants = []) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const crop = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(centeredCropForImage(image));
      image.onerror = () => reject(new Error('Invalid image file.'));
      image.src = objectUrl;
    });
    return Promise.all(
      variants.map(({ size, fileName }) => createSquareCroppedImage({
        file,
        crop,
        size,
        mimeType: 'image/jpeg',
        fileName,
      }))
    );
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function createArtistProfileImages(file) {
  return createSquareImageSet(file, [
    { size: PROFILE_ORIGINAL_MAX_SIZE, fileName: 'artist-profile-original.jpg' },
    { size: PROFILE_IMAGE_SIZE, fileName: 'artist-profile-1000.jpg' },
    { size: PROFILE_THUMB_SIZE, fileName: 'artist-profile-200.jpg' },
    { size: PROFILE_TINY_SIZE, fileName: 'artist-profile-50.jpg' },
  ]);
}

function formatTimestamp(timestamp) {
  const millis = timestamp?.toMillis?.() || timestamp?.millis || 0;
  return millis ? new Date(millis).toLocaleString() : 'Not available';
}

function buildTemporaryArtistId() {
  return `temp-artist-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function sortArtists(list = []) {
  return [...list].sort((left, right) => {
    const rightMillis = right.updatedAt?.toMillis?.() || right.createdAt?.toMillis?.() || 0;
    const leftMillis = left.updatedAt?.toMillis?.() || left.createdAt?.toMillis?.() || 0;
    return rightMillis - leftMillis;
  });
}

export default function TemporaryArtistsAdmin() {
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [photoAsset, setPhotoAsset] = useState(EMPTY_PHOTO);
  const [audioAsset, setAudioAsset] = useState(EMPTY_AUDIO);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState('');
  const [removeAudio, setRemoveAudio] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const photoPreviewRef = useRef('');
  const audioPreviewRef = useRef('');

  const selectedArtist = useMemo(
    () => artists.find((artist) => artist.id === selectedId) || null,
    [artists, selectedId]
  );
  const currentPhotoPath = photoPreview ? '' : (photoAsset.thumbPath || photoAsset.path);
  const currentPhotoUrl = useStorageUrl(currentPhotoPath);
  const currentAudioStorageUrl = useStorageUrl(audioPreview ? '' : audioAsset.path);
  const currentAudioUrl = audioPreview || audioAsset.url || currentAudioStorageUrl || '';

  useEffect(() => () => {
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  useEffect(() => () => {
    if (audioPreview?.startsWith('blob:')) URL.revokeObjectURL(audioPreview);
  }, [audioPreview]);

  useEffect(() => {
    photoPreviewRef.current = photoPreview;
  }, [photoPreview]);

  useEffect(() => {
    audioPreviewRef.current = audioPreview;
  }, [audioPreview]);

  const loadArtists = useCallback(async (preferredId = '') => {
    try {
      setLoading(true);
      setError('');
      const snap = await getDocs(query(collection(db, 'artists'), where('profileType', '==', 'temporary')));
      const nextArtists = sortArtists(
        snap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }))
      );
      setArtists(nextArtists);
      const resolvedId = preferredId && nextArtists.some((artist) => artist.id === preferredId)
        ? preferredId
        : nextArtists[0]?.id || '';
      setSelectedId((current) => preferredId ? resolvedId : (current && nextArtists.some((artist) => artist.id === current) ? current : resolvedId));
    } catch (err) {
      setArtists([]);
      setSelectedId('');
      setError(err?.message || 'Unable to load temporary artists.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArtists();
  }, [loadArtists]);

  useEffect(() => {
    if (!selectedArtist) {
      setForm(EMPTY_FORM);
      setPhotoAsset(EMPTY_PHOTO);
      setAudioAsset(EMPTY_AUDIO);
      setPhotoFile(null);
      setAudioFile(null);
      setRemoveAudio(false);
      if (photoPreviewRef.current?.startsWith('blob:')) URL.revokeObjectURL(photoPreviewRef.current);
      if (audioPreviewRef.current?.startsWith('blob:')) URL.revokeObjectURL(audioPreviewRef.current);
      setPhotoPreview('');
      setAudioPreview('');
      return;
    }

    setForm({
      artistName: selectedArtist.artistFullName || '',
      description: selectedArtist.artistDescription || '',
      instagram: selectedArtist.instagram || '',
      twitter: selectedArtist.twitter || '',
      tiktok: selectedArtist.tiktok || '',
      youtube: selectedArtist.youtube || '',
      website: selectedArtist.website || '',
    });
    setPhotoAsset({
      originalPath: selectedArtist.artistProfilePhotoOriginalPath || '',
      path: selectedArtist.artistProfilePhotoPath || selectedArtist.artistProfilePhoto1000Path || '',
      thumbPath: selectedArtist.artistProfilePhotoThumbPath || selectedArtist.artistProfilePhoto200Path || '',
      tinyPath: selectedArtist.artistProfilePhoto50Path || '',
    });
    setAudioAsset({
      url: selectedArtist.artistAudioUrl || '',
      path: selectedArtist.artistAudioPath || '',
    });
    setPhotoFile(null);
    setAudioFile(null);
    setRemoveAudio(false);
    if (photoPreviewRef.current?.startsWith('blob:')) URL.revokeObjectURL(photoPreviewRef.current);
    if (audioPreviewRef.current?.startsWith('blob:')) URL.revokeObjectURL(audioPreviewRef.current);
    setPhotoPreview('');
    setAudioPreview('');
  }, [selectedArtist]);

  const handleCreateNew = () => {
    setSelectedId('');
    setNotice('');
    setError('');
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file for the artist photo.');
      return;
    }
    setError('');
    setPhotoFile(file);
    if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleAudioChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      setError('Choose an audio file.');
      return;
    }
    setError('');
    setAudioFile(file);
    setRemoveAudio(false);
    if (audioPreview?.startsWith('blob:')) URL.revokeObjectURL(audioPreview);
    setAudioPreview(URL.createObjectURL(file));
  };

  const uploadArtistPhoto = async (artistId) => {
    if (!photoFile) return photoAsset;
    const [originalFile, largeFile, thumbFile, tinyFile] = await createArtistProfileImages(photoFile);
    const versionToken = createVersionToken();
    const photoOriginalPath = `artists/${artistId}/profile-original-v${versionToken}.jpg`;
    const photoPath = `artists/${artistId}/profile-1000-v${versionToken}.jpg`;
    const photoThumbPath = `artists/${artistId}/profile-200-v${versionToken}.jpg`;
    const photoTinyPath = `artists/${artistId}/profile-50-v${versionToken}.jpg`;
    const uploads = [
      { path: photoOriginalPath, file: originalFile },
      { path: photoPath, file: largeFile },
      { path: photoThumbPath, file: thumbFile },
      { path: photoTinyPath, file: tinyFile },
    ];

    await Promise.all(uploads.map(({ path, file }) => new Promise((resolve, reject) => {
      uploadBytesResumable(ref(storage, path), file, {
        contentType: file.type || 'image/jpeg',
      }).on('state_changed', () => {}, reject, resolve);
    })));

    return {
      originalPath: photoOriginalPath,
      path: photoPath,
      thumbPath: photoThumbPath,
      tinyPath: photoTinyPath,
    };
  };

  const uploadArtistAudio = async (artistId) => {
    if (removeAudio) return EMPTY_AUDIO;
    if (!audioFile) return audioAsset;
    const extension = String(audioFile.name || '').split('.').pop()?.toLowerCase() || 'mp3';
    const versionToken = createVersionToken();
    const audioPath = `artists/${artistId}/audio-v${versionToken}.${extension}`;
    await new Promise((resolve, reject) => {
      uploadBytesResumable(ref(storage, audioPath), audioFile, {
        contentType: audioFile.type || 'audio/mpeg',
      }).on('state_changed', () => {}, reject, resolve);
    });
    return {
      url: '',
      path: audioPath,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const artistId = selectedArtist?.artistId || buildTemporaryArtistId();
    try {
      setSaving(true);
      setError('');
      setNotice('');
      const uploadedPhoto = await uploadArtistPhoto(artistId);
      if (!uploadedPhoto.path) {
        throw new Error('Upload an artist photo.');
      }
      const uploadedAudio = await uploadArtistAudio(artistId);
      const callableName = selectedArtist ? 'updateTemporaryArtist' : 'createTemporaryArtist';
      const callable = httpsCallable(functions, callableName);
      const response = await callable({
        ...(selectedArtist ? { artistId } : { artistId }),
        input: {
          ...form,
          photoOriginalPath: uploadedPhoto.originalPath,
          photoPath: uploadedPhoto.path,
          photoThumbPath: uploadedPhoto.thumbPath,
          photo50Path: uploadedPhoto.tinyPath,
          audioPath: uploadedAudio.path,
          audioUrl: uploadedAudio.url,
        },
      });
      const savedArtistId = response?.data?.artistId || artistId;
      setNotice(selectedArtist ? 'Temporary artist updated.' : 'Temporary artist created.');
      await loadArtists(savedArtistId);
    } catch (err) {
      setError(err?.message || 'Unable to save temporary artist.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedArtist?.id) return;
    const confirmed = window.confirm(
      `Delete ${selectedArtist.artistFullName || 'this temporary artist'} permanently? This removes the artist page, all fan records under the artist, and all user follow references.`
    );
    if (!confirmed) return;
    try {
      setDeleting(true);
      setError('');
      setNotice('');
      const callable = httpsCallable(functions, 'deleteTemporaryArtist');
      await callable({ artistId: selectedArtist.id });
      setNotice('Temporary artist deleted.');
      await loadArtists('');
    } catch (err) {
      setError(err?.message || 'Unable to delete temporary artist.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/admin')} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Admin</span>
          <h1>Temporary Artists</h1>
          <p>Create placeholder artist pages that appear in discovery and collect fans without creating artist accounts, Stripe profiles, or creator access.</p>
        </header>

        {(notice || error) ? (
          <section className={styles.noticeCard}>
            {notice ? <p className={styles.noticeText}>{notice}</p> : null}
            {error ? <p className={styles.errorText}>{error}</p> : null}
          </section>
        ) : null}

        <section className={styles.workspace}>
          <section className={styles.queueCard}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Queue</span>
                <h2>Temporary Artists</h2>
              </div>
              <button type="button" className={styles.primaryButton} onClick={handleCreateNew}>
                <FiPlus />
                <span>New Temp Artist</span>
              </button>
            </div>

            {loading ? <p className={styles.state}>Loading temporary artists…</p> : null}
            {!loading && !artists.length ? (
              <div className={styles.emptyState}>
                <strong>No temporary artists yet.</strong>
                <p>Create one to publish a public artist page and start collecting fans before the real artist account exists.</p>
              </div>
            ) : null}

            {artists.length ? (
              <div className={styles.queueList}>
                {artists.map((artist) => (
                  <button
                    key={artist.id}
                    type="button"
                    className={`${styles.queueItem} ${selectedId === artist.id ? styles.queueItemActive : ''}`.trim()}
                    onClick={() => {
                      setSelectedId(artist.id);
                      setNotice('');
                      setError('');
                    }}
                  >
                    <div className={styles.queuePhoto}>
                      <ResolvedStorageImage
                        path={artist.artistProfilePhotoThumbPath || artist.artistProfilePhoto200Path || artist.artistProfilePhotoPath || ''}
                        alt={artist.artistFullName || 'Temporary artist'}
                        fallback={<div className={styles.queuePlaceholder}><FiUser /></div>}
                      />
                    </div>
                    <div className={styles.queueCopy}>
                      <strong>{artist.artistFullName || 'Untitled Artist'}</strong>
                      <p>{Number(artist.fanCount || 0)} fans</p>
                      <span>{formatTimestamp(artist.updatedAt || artist.createdAt)}</span>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className={styles.detailCard}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Editor</span>
                <h2>{selectedArtist ? 'Edit Temporary Artist' : 'Create Temporary Artist'}</h2>
              </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <FormField label="Artist Name">
                <input value={form.artistName} onChange={(event) => setForm((prev) => ({ ...prev, artistName: event.target.value }))} required />
              </FormField>

              <FormField label="Description">
                <textarea value={form.description} onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))} rows={5} />
              </FormField>

              <FormField label="Artist Photo" helper="Required. Uploads original, 1000, 200, and 50 pixel square variants into Firebase Storage.">
                <input type="file" accept="image/*" onChange={handlePhotoChange} />
                {photoPreview || currentPhotoUrl ? (
                  <div className={styles.previewCard}>
                    <img src={photoPreview || currentPhotoUrl} alt="Artist preview" className={styles.previewImage} />
                  </div>
                ) : null}
              </FormField>

              <FormField label="Artist Audio" helper="Optional. Upload a single audio track for the public artist page play button.">
                <input type="file" accept="audio/*" onChange={handleAudioChange} />
                {currentAudioUrl ? (
                  <div className={styles.audioCard}>
                    <span className={styles.audioLabel}><FiMusic /> Audio Preview</span>
                    <audio controls src={currentAudioUrl} className={styles.audioPlayer} />
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={() => {
                        setRemoveAudio(true);
                        setAudioFile(null);
                        if (audioPreview?.startsWith('blob:')) URL.revokeObjectURL(audioPreview);
                        setAudioPreview('');
                        setAudioAsset(EMPTY_AUDIO);
                      }}
                    >
                      Remove Audio
                    </button>
                  </div>
                ) : null}
              </FormField>

              <div className={styles.grid}>
                <FormField label="Instagram">
                  <input value={form.instagram} onChange={(event) => setForm((prev) => ({ ...prev, instagram: event.target.value }))} />
                </FormField>
                <FormField label="Twitter / X">
                  <input value={form.twitter} onChange={(event) => setForm((prev) => ({ ...prev, twitter: event.target.value }))} />
                </FormField>
                <FormField label="TikTok">
                  <input value={form.tiktok} onChange={(event) => setForm((prev) => ({ ...prev, tiktok: event.target.value }))} />
                </FormField>
                <FormField label="YouTube">
                  <input value={form.youtube} onChange={(event) => setForm((prev) => ({ ...prev, youtube: event.target.value }))} />
                </FormField>
              </div>

              <FormField label="Website">
                <input value={form.website} onChange={(event) => setForm((prev) => ({ ...prev, website: event.target.value }))} />
              </FormField>

              {selectedArtist ? (
                <div className={styles.metaRow}>
                  <span>Artist ID: {selectedArtist.artistId || selectedArtist.id}</span>
                  <span>Fans: {Number(selectedArtist.fanCount || 0)}</span>
                  <span>Status: {selectedArtist.placeholderStatus || 'active'}</span>
                </div>
              ) : null}

              <div className={styles.warningCard}>
                <strong>Delete is permanent.</strong>
                <p>Removing a temporary artist deletes the public artist page, every `artists/{'{artistId}'}/fans` record, and every `users/{'{uid}'}/follows/{'{artistId}'}` reference.</p>
              </div>

              <div className={styles.actionRow}>
                <button type="submit" className={styles.primaryButton} disabled={saving || deleting}>
                  {saving ? 'Saving…' : (selectedArtist ? 'Save Changes' : 'Create Temporary Artist')}
                </button>
                {selectedArtist ? (
                  <button type="button" className={styles.dangerButton} onClick={handleDelete} disabled={saving || deleting}>
                    <FiTrash2 />
                    <span>{deleting ? 'Deleting…' : 'Delete Temporary Artist'}</span>
                  </button>
                ) : null}
              </div>
            </form>
          </section>
        </section>
      </div>
    </div>
  );
}
